import * as React from 'react'
// PRO-130 note: these are deep `antd/es/*` subpaths, never the `antd` root
// barrel — the barrel would weld antd's whole component surface onto whatever
// chunk this lands in. Both are CONTEXTS, so with the toggle on this branch
// keeps reading what `<ConfigProvider componentDisabled>` / `<Form disabled>`
// and the surrounding `Form.Item` publish, exactly as antd's Select does.
import DisabledContext from 'antd/es/config-provider/DisabledContext'
import { FormItemInputContext } from 'antd/es/form/context'
import type {
  DefaultOptionType,
  LabeledValue,
  RefSelectProps,
  SelectProps,
} from 'antd/es/select'

import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from 'src/components/ui/command'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from 'src/components/ui/popover'
import {
  CheckOutlined,
  CloseCircleFilled,
  CloseOutlined,
  DownOutlined,
  LoadingOutlined,
} from 'src/icons/antd-compat'
import { cn } from 'src/lib/utils'

import {
  convertChildrenToOptions,
  createTagOption,
  DisplayValue,
  fillFieldNames,
  filterRawOptions,
  isGroupEntry,
  normalizeOptions,
  NormalizedOption,
  toLabeledValues,
} from './options'
import type { RawValueType } from './types'
import { useMergedState } from './useMergedState'
import { Empty } from '../Empty'
import { omitTransient } from '../overlay/placement'
import { useStableId } from '../overlay/useStableId'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Select` seam — antd's Select rebuilt on the
 * Radix `Popover` plus `cmdk` (`src/components/ui/{popover,command}`) behind
 * antd's own `SelectProps`. `./index.tsx` is the dispatcher that chooses
 * between this and `./Select.antd`; nothing else imports this file, and
 * nothing reaches it until someone flips the design toggle
 * (`src/design-mode`).
 *
 * The external API is antd's `SelectProps`, so the 280 files that import
 * `Select` from `src/antd` — and everything wrapping it: `src/ui/TagInput`,
 * `src/ui/useSelect`, `src/new-components/select/*`, `SelectFilled` — switch
 * with no edit at all.
 *
 * LAYOUT, and why it is shaped like this:
 *
 *   <Popover>                        ← Radix: positioning, portal, dismissal
 *     <Command>                      ← cmdk root: OWNS the whole control, so
 *       <PopoverAnchor>                the inline search input shares its
 *         …selector / tags / input…    context and keydowns reach cmdk's
 *       </PopoverAnchor>               navigation
 *       <PopoverContent>             ← portalled; cmdk only ever queries
 *         <CommandList>…</CommandList>  through `CommandList`'s own ref, so
 *       </PopoverContent>              portalling the list out of the root is
 *     </Command>                       supported
 *   </Popover>
 *
 * ---------------------------------------------------------------------------
 * THE CONTAINER DECISION, and where it differs from the Tooltip/Modal batch
 *
 * The popup mounts INSIDE THE CONTROL by default, not on `document.body` — and
 * that is deliberately the opposite of what `../Tooltip.shadcn` decided for
 * the two portal seams before it.
 *
 * Inside the control is what antd already does here. `./Select.antd` has
 * defaulted `getPopupContainer` to `trigger.parentElement` for as long as the
 * seam has existed, and antd hands that callback the SELECTOR — so the popup
 * ends up a direct child of the `.ant-select` root, not a sibling of it
 * (verified against the rendered DOM; `select-seam-parity.test.tsx` asserts
 * both halves rather than leaving it here as a claim). This branch therefore
 * defaults to its own root and passes `getPopupContainer` the same selector
 * node, so a call site written as `(trigger) => trigger.parentNode` still
 * means what it meant.
 *
 * The reason it is not simply re-homed to the body: this is a PORT, not a
 * redesign. 23 call sites pass their own container to escape the default (most
 * of them `() => document.body`), and a dozen stylesheets reach the popup by
 * descending from a `styled(Select)` class — which sits on the root the popup
 * is inside. Re-homing every Select would move all of that at once, under a
 * toggle whose whole premise is that behaviour is preserved. A tooltip could
 * be re-homed because nothing depends on where a tooltip lands; a Select popup
 * is a surface people have written CSS against.
 *
 * What DOES change, and improves things without being asked to: Radix
 * positions with `strategy: 'fixed'`, so an ancestor with `overflow: hidden`
 * no longer clips the popup the way antd's absolutely-positioned one is
 * clipped — unless that ancestor also establishes a containing block (a
 * `transform`/`filter`/`will-change`), in which case both branches clip alike.
 * The mount POINT — which is what a `getPopupContainer` call site is asking
 * about — is the same node in both modes.
 *
 * ---------------------------------------------------------------------------
 * PARITY NOTES (verified against rc-select 14.x / antd 5.15.4, the versions
 * this repo pins; the filtering and option rules live in `./options`)
 *
 *   • `onChange(value, option)` keeps antd's exact payload: the raw value (or
 *     `{ value, label, key }` under `labelInValue`), single vs array by
 *     `mode`, and it fires only when the value actually CHANGED.
 *   • Selected labels are cached, so an option that drops out of `options`
 *     after an async search keeps its label instead of flashing a raw id
 *     (rc-select's `useCache`; `AsyncSelectWithFetchMore` relies on it).
 *   • `popupMatchSelectWidth` → the `--radix-popover-trigger-width` var.
 *   • THE DOM CONTRACT IS `data-slot="select-*"`, NOT `.ant-select-*`. antd's
 *     own Select styles are still injected on any page that renders a
 *     TreeSelect, a Cascader or a Table page-size changer, and antd's sheets
 *     are UNLAYERED — so re-emitting its class names would put antd's rules
 *     and this seam's utilities in a fight the utilities lose. Every live
 *     stylesheet that reached `.ant-select-*` grew a `[data-slot='select-*']`
 *     selector beside it in the same PR.
 *
 * NOT PORTED — named rather than pretended:
 *   • `mode="combobox"` / `SECRET_COMBOBOX_MODE_DO_NOT_USE`. antd's own
 *     `SelectProps` does not type it and no call site in this repo uses it;
 *     a `mode` this branch does not know is treated as single-select. The
 *     static stays on the component so the shape is unchanged.
 *   • `virtual` / `listItemHeight`. The cmdk list is not windowed, so a very
 *     long option list renders in full. `listHeight` is honoured (it caps the
 *     scroll box), and the only two call sites that set `virtual` set it to
 *     `false`.
 *   • `menuItemSelectedIcon` as a CUSTOM icon — antd's DEFAULT (a trailing
 *     check on every selected option in multiple/tags mode) IS rendered.
 *     `defaultActiveFirstOption`, `builtinPlacements`,
 *     `dropdownAlign`, `showAction`, `backfill` — no call site sets any.
 * ------------------------------------------------------------------------- */

/** antd's own `any` default for the value type — see `./types`. */
type Props = SelectProps<$TSFixMe> & { id?: string }

const EMPTY_OPTIONS: DefaultOptionType[] = []

/**
 * Props that must never reach the root `<div>`. rc-select strips its own set
 * the same way (`DEFAULT_OMIT_PROPS` in rc-select's BaseSelect);
 * `theme`/`forwardedAs` are what styled-components injects when a call site
 * does `styled(Select)` — `TagInput`, `SelectFilled`, `ColorPicker` and
 * `RangeSelectorWithRadio` all do.
 */
const NON_DOM_PROPS = new Set([
  'theme',
  'forwardedAs',
  'prefixCls',
  'inputValue',
  'showAction',
  'backfill',
  'getInputElement',
  'getRawInputElement',
  'omitDomProps',
  'choiceTransitionName',
  'transitionName',
  'dropdownAlign',
  'animation',
  'builtinPlacements',
  'internalProps',
  'direction',
])

/**
 * antd puts the caller's `aria-*` on BOTH the root and the inner combobox
 * input, which is what makes `getByRole('combobox', { name })` resolve for the
 * call sites that pass an `aria-label`. Mirror it.
 */
const pickAriaProps = (rest: Record<string, unknown>) => {
  const out: Record<string, unknown> = {}
  Object.keys(rest).forEach((key) => {
    if (key.startsWith('aria-')) out[key] = rest[key]
  })
  return out
}

const toDomProps = (rest: Record<string, unknown>) => {
  const out: Record<string, unknown> = {}
  Object.keys(omitTransient(rest)).forEach((key) => {
    if (!NON_DOM_PROPS.has(key)) out[key] = rest[key]
  })
  return out as Omit<React.HTMLAttributes<HTMLDivElement>, 'defaultValue'>
}

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&')

const sizeClassName = (size: Props['size'], multiple: boolean) => {
  if (size === 'small') return multiple ? 'min-h-6 py-0' : 'h-6'
  if (size === 'large') return multiple ? 'min-h-10 py-1' : 'h-10'
  return multiple ? 'min-h-9 py-0.5' : 'h-9'
}

const variantClassName = (variant: Props['variant']) => {
  if (variant === 'borderless')
    return 'border-transparent bg-transparent shadow-none'
  if (variant === 'filled') return 'border-transparent bg-muted shadow-none'
  return undefined
}

/** antd's `maxTagPlaceholder` default. */
const defaultMaxTagPlaceholder = (omitted: unknown[]) =>
  `+ ${omitted.length} ...`

const truncate = (label: React.ReactNode, max?: number) => {
  if (max == null || typeof label !== 'string' || label.length <= max)
    return label
  return `${label.slice(0, max)}...`
}

/**
 * A click inside the dropdown must not blur the inline search input — that
 * would close the popup before the click lands. Keep the browser's default
 * only for elements that legitimately want focus, so a text input inside a
 * `dropdownRender` panel still works.
 */
const keepsOwnFocus = (target: EventTarget | null) => {
  const el = target as HTMLElement | null
  if (!el || typeof el.closest !== 'function') return false
  return Boolean(
    el.closest('input, textarea, select, [contenteditable="true"]')
  )
}

const SelectShadcn = React.forwardRef<RefSelectProps, Props>((props, ref) => {
  const {
    // value
    value: valueProp,
    defaultValue,
    onChange,
    onSelect,
    onDeselect,
    labelInValue = false,
    mode,
    maxCount,
    // options
    options: optionsProp,
    children,
    fieldNames,
    optionFilterProp,
    optionLabelProp,
    filterOption,
    filterSort,
    optionRender,
    labelRender,
    // search
    showSearch: showSearchProp,
    searchValue: searchValueProp,
    onSearch,
    autoClearSearchValue = true,
    tokenSeparators,
    defaultActiveFirstOption: _defaultActiveFirstOption,
    // popup
    open: openProp,
    defaultOpen,
    onDropdownVisibleChange,
    dropdownRender,
    dropdownStyle,
    popupClassName,
    dropdownClassName,
    popupMatchSelectWidth,
    dropdownMatchSelectWidth,
    listHeight = 256,
    listItemHeight: _listItemHeight,
    virtual: _virtual,
    getPopupContainer,
    placement,
    onPopupScroll,
    // presentation
    placeholder,
    notFoundContent,
    loading = false,
    disabled: disabledProp,
    allowClear,
    clearIcon,
    suffixIcon,
    removeIcon,
    menuItemSelectedIcon: _menuItemSelectedIcon,
    showArrow,
    size,
    bordered,
    variant,
    status: statusProp,
    rootClassName,
    autoFocus,
    className,
    style,
    id: idProp,
    // tags
    maxTagCount,
    maxTagTextLength,
    maxTagPlaceholder,
    tagRender,
    // events
    onClear,
    onBlur,
    onFocus,
    onKeyDown,
    onInputKeyDown,
    ...rest
  } = props

  const multiple = mode === 'multiple' || mode === 'tags'
  const showSearch = showSearchProp ?? multiple

  const contextDisabled = React.useContext(DisabledContext)
  const formItemContext = React.useContext(FormItemInputContext)
  const disabled = disabledProp ?? contextDisabled
  const status = statusProp ?? formItemContext?.status
  const inFormItem = Boolean(formItemContext?.isFormItemInput)

  const generatedId = useStableId('wh-select')
  const inputId = idProp ?? generatedId
  // cmdk owns the listbox element's own id, so `aria-controls` points at the
  // wrapper the seam renders around it instead (still the popup container).
  const listboxId = `${inputId}_list`

  // The root is held in STATE as well as a ref: the portal container is
  // resolved DURING render, and a ref is still `null` on the first pass — so a
  // Select that is open from mount (`defaultOpen`, or an `open` a parent drives
  // itself) would portal to the body instead of into itself. The state write
  // gives it the one extra render it needs. `rootRef` stays for the synchronous
  // `contains()` checks in the dismissal guards.
  const rootRef = React.useRef<HTMLDivElement | null>(null)
  const [rootNode, setRootNode] = React.useState<HTMLDivElement | null>(null)
  const setRoot = React.useCallback((node: HTMLDivElement | null) => {
    rootRef.current = node
    setRootNode(node)
  }, [])
  // antd hands `getPopupContainer` the SELECTOR, not the root — which is why
  // `(trigger) => trigger.parentElement` (this seam's own antd default) lands
  // the popup inside the root. Keeping the same node means a call site's own
  // `getPopupContainer` gets what it has always been given.
  const selectorRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)
  const labelCacheRef = React.useRef(new Map<RawValueType, LabeledValue>())

  // ------------------------------------------------------------- open state
  const [mergedOpen, setMergedOpen] = useMergedState<boolean | undefined>(
    false,
    { value: openProp, defaultValue: defaultOpen }
  )
  const open = Boolean(mergedOpen) && !disabled
  const setOpen = React.useCallback(
    (next: boolean) => {
      setMergedOpen(next)
      if (next !== open) onDropdownVisibleChange?.(next)
    },
    [onDropdownVisibleChange, open, setMergedOpen]
  )

  // ----------------------------------------------------------- search state
  const [mergedSearch, setMergedSearch] = useMergedState<string | undefined>(
    '',
    { value: searchValueProp }
  )
  const searchValue = mergedSearch ?? ''
  // rc-select only reaches `props.onSearch` from its user-typing path
  // (`onInternalSearch` with `fromTyping`); every other write — the clear after
  // a selection, the clear on blur, the clear from `allowClear` — is a plain
  // state write. `setSearchValueRaw` is that plain write.
  const setSearchValueRaw = React.useCallback(
    (next: string) => {
      setMergedSearch(next)
    },
    [setMergedSearch]
  )
  const setSearchValue = React.useCallback(
    (next: string) => {
      setMergedSearch(next)
      if (next !== searchValue) onSearch?.(next)
    },
    [onSearch, searchValue, setMergedSearch]
  )

  // ------------------------------------------------------------ value state
  const [rawValue, setRawValue] = useMergedState<unknown>(undefined, {
    value: valueProp,
    defaultValue,
  })

  // ---------------------------------------------------------------- options
  const childrenOptions = React.useMemo(
    () =>
      children != null ? convertChildrenToOptions(children) : EMPTY_OPTIONS,
    [children]
  )
  const childrenAsData = childrenOptions.length > 0
  const mergedFieldNames = React.useMemo(
    () => fillFieldNames(fieldNames, childrenAsData),
    [childrenAsData, fieldNames]
  )
  const baseOptions = childrenAsData
    ? childrenOptions
    : optionsProp ?? EMPTY_OPTIONS

  /** value → raw option, over the UNFILTERED list (labels must survive search). */
  const valueOptions = React.useMemo(() => {
    const map = new Map<RawValueType, DefaultOptionType>()
    const walk = (list: DefaultOptionType[]) => {
      list.forEach((item) => {
        const nested = item[mergedFieldNames.options] as
          | DefaultOptionType[]
          | undefined
        if (nested) {
          walk(nested)
          return
        }
        const val = item[mergedFieldNames.value] as RawValueType
        if (val !== undefined && val !== null) map.set(val, item)
      })
    }
    walk(baseOptions as DefaultOptionType[])
    return map
  }, [baseOptions, mergedFieldNames])

  const displayValues = React.useMemo<DisplayValue[]>(
    () =>
      toLabeledValues(rawValue, {
        multiple,
        valueOptions,
        labelCache: labelCacheRef.current,
        fieldNames: mergedFieldNames,
        optionLabelProp,
      }).map((item) => ({
        ...item,
        label:
          (typeof labelRender === 'function'
            ? labelRender(item)
            : item.label) ?? item.value,
      })),
    [
      labelRender,
      mergedFieldNames,
      multiple,
      optionLabelProp,
      rawValue,
      valueOptions,
    ]
  )

  // Refresh the label cache from whatever could be resolved this render.
  React.useEffect(() => {
    displayValues.forEach((item) => {
      if (item.label !== undefined && item.label !== item.value) {
        labelCacheRef.current.set(item.value, item)
      }
    })
  }, [displayValues])

  const selectedValues = React.useMemo(
    () => new Set(displayValues.map((item) => item.value)),
    [displayValues]
  )

  // `tags` mode: a value with no option of its own gets one, so it renders in
  // the list.
  const filledTagOptions = React.useMemo(() => {
    if (mode !== 'tags') return baseOptions as DefaultOptionType[]
    const clone = [...(baseOptions as DefaultOptionType[])]
    ;[...displayValues]
      .sort((a, b) => (a.value < b.value ? -1 : 1))
      .forEach((item) => {
        if (!valueOptions.has(item.value)) {
          clone.push(createTagOption(item.value, item.label, mergedFieldNames))
        }
      })
    return clone
  }, [baseOptions, displayValues, mergedFieldNames, mode, valueOptions])

  const filteredOptions = React.useMemo(
    () =>
      filterRawOptions(filledTagOptions, {
        searchValue,
        filterOption,
        optionFilterProp,
        fieldNames: mergedFieldNames,
      }),
    [
      filledTagOptions,
      filterOption,
      mergedFieldNames,
      optionFilterProp,
      searchValue,
    ]
  )

  // `tags` mode: offer the free-typed search value as a new option.
  const filledSearchOptions = React.useMemo(() => {
    if (mode !== 'tags' || !searchValue) return filteredOptions
    const key = optionFilterProp || mergedFieldNames.value
    if (
      filteredOptions.some(
        (item) =>
          item[key] === searchValue ||
          item[mergedFieldNames.value] === searchValue
      )
    ) {
      return filteredOptions
    }
    return [
      createTagOption(searchValue, searchValue, mergedFieldNames),
      ...filteredOptions,
    ]
  }, [filteredOptions, mergedFieldNames, mode, optionFilterProp, searchValue])

  const orderedOptions = React.useMemo(
    () =>
      filterSort
        ? [...filledSearchOptions].sort((a, b) => filterSort(a, b))
        : filledSearchOptions,
    [filledSearchOptions, filterSort]
  )

  const { entries, flat } = React.useMemo(
    () => normalizeOptions(orderedOptions, mergedFieldNames),
    [mergedFieldNames, orderedOptions]
  )

  /** cmdk needs a unique string per item; de-duplicate defensively. */
  const itemKeys = React.useMemo(() => {
    const used = new Set<string>()
    const map = new Map<NormalizedOption, string>()
    flat.forEach((option, index) => {
      let key = option.key || `option-${index}`
      while (used.has(key)) key = `${key}-${index}`
      used.add(key)
      map.set(option, key)
    })
    return map
  }, [flat])

  // ---------------------------------------------------- cmdk active option
  const [activeKey, setActiveKey] = React.useState<string | undefined>()

  // On open, highlight the selected option (antd scrolls to it) rather than
  // whatever cmdk would auto-pick.
  React.useEffect(() => {
    if (!open) return
    const selected = flat.find((option) => selectedValues.has(option.value))
    if (selected) setActiveKey(itemKeys.get(selected))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // ----------------------------------------------------------------- change
  const emitChange = React.useCallback(
    (nextLabeled: DisplayValue[]) => {
      const changed =
        nextLabeled.length !== displayValues.length ||
        nextLabeled.some(
          (item, index) => displayValues[index]?.value !== item.value
        )

      const nextInternal = labelInValue
        ? nextLabeled
        : nextLabeled.map((item) => item.value)
      setRawValue(multiple ? nextInternal : nextInternal[0])
      if (!changed || !onChange) return

      const returnValues = labelInValue
        ? nextLabeled
        : nextLabeled.map((item) => item.value)
      const returnOptions = nextLabeled.map(
        (item) =>
          valueOptions.get(item.value) ??
          createTagOption(item.value, item.label, mergedFieldNames)
      )
      onChange(
        (multiple ? returnValues : returnValues[0]) as never,
        (multiple ? returnOptions : returnOptions[0]) as never
      )
    },
    [
      displayValues,
      labelInValue,
      mergedFieldNames,
      multiple,
      onChange,
      setRawValue,
      valueOptions,
    ]
  )

  const toLabeled = React.useCallback(
    (value: RawValueType, label?: React.ReactNode): DisplayValue => {
      const option = valueOptions.get(value)
      return {
        value,
        key: option?.key != null ? String(option.key) : String(value),
        label:
          label ??
          (option
            ? (option[
                optionLabelProp || mergedFieldNames.label
              ] as React.ReactNode)
            : labelCacheRef.current.get(value)?.label ?? value),
      }
    },
    [mergedFieldNames.label, optionLabelProp, valueOptions]
  )

  const selectValue = React.useCallback(
    (value: RawValueType, label?: React.ReactNode) => {
      const labeled = toLabeled(value, label)
      const option =
        valueOptions.get(value) ??
        createTagOption(value, labeled.label, mergedFieldNames)

      if (!multiple) {
        emitChange([labeled])
        onSelect?.((labelInValue ? labeled : value) as never, option as never)
        setOpen(false)
        setSearchValueRaw('')
        return
      }

      if (selectedValues.has(value)) {
        emitChange(displayValues.filter((item) => item.value !== value))
        onDeselect?.((labelInValue ? labeled : value) as never, option as never)
      } else {
        if (maxCount != null && displayValues.length >= maxCount) return
        emitChange([...displayValues, labeled])
        onSelect?.((labelInValue ? labeled : value) as never, option as never)
      }
      if (autoClearSearchValue) setSearchValueRaw('')
    },
    [
      autoClearSearchValue,
      displayValues,
      emitChange,
      labelInValue,
      maxCount,
      mergedFieldNames,
      multiple,
      onDeselect,
      onSelect,
      selectedValues,
      setOpen,
      setSearchValueRaw,
      toLabeled,
      valueOptions,
    ]
  )

  const removeValue = React.useCallback(
    (value: RawValueType) => {
      emitChange(displayValues.filter((item) => item.value !== value))
      const option =
        valueOptions.get(value) ??
        createTagOption(value, undefined, mergedFieldNames)
      onDeselect?.(
        (labelInValue ? toLabeled(value) : value) as never,
        option as never
      )
    },
    [
      displayValues,
      emitChange,
      labelInValue,
      mergedFieldNames,
      onDeselect,
      toLabeled,
      valueOptions,
    ]
  )

  const clearAll = React.useCallback(() => {
    // antd clears to `[]` for multiple and `undefined` for single.
    setRawValue(multiple ? [] : undefined)
    if (displayValues.length && onChange) {
      onChange(
        (multiple ? [] : undefined) as never,
        (multiple ? [] : undefined) as never
      )
    }
    setSearchValueRaw('')
    onClear?.()
  }, [
    displayValues.length,
    multiple,
    onChange,
    onClear,
    setRawValue,
    setSearchValueRaw,
  ])

  // ----------------------------------------------------------- search input
  const commitTokens = React.useCallback(
    (text: string): boolean => {
      if (!tokenSeparators?.length || !multiple) return false
      if (!tokenSeparators.some((sep) => text.includes(sep))) return false
      const tokens = text
        .split(new RegExp(`[${tokenSeparators.map(escapeRegExp).join('')}]`))
        .map((token) => token.trim())
        .filter(Boolean)
      const next = [...displayValues]
      tokens.forEach((token) => {
        if (next.some((item) => String(item.value) === token)) return
        if (maxCount != null && next.length >= maxCount) return
        next.push(toLabeled(token))
      })
      emitChange(next)
      setSearchValue('')
      return true
    },
    [
      displayValues,
      emitChange,
      maxCount,
      multiple,
      setSearchValue,
      toLabeled,
      tokenSeparators,
    ]
  )

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value
    if (commitTokens(next)) return
    if (!open && next) setOpen(true)
    setSearchValue(next)
  }

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    onInputKeyDown?.(event)
    onKeyDown?.(event as never)
    if (event.defaultPrevented) return

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        if (!open) setOpen(true)
        break
      case 'Escape':
        if (open) {
          event.preventDefault()
          event.stopPropagation()
          setOpen(false)
        }
        break
      case 'Enter':
        // With the list closed there is no cmdk item to activate, so `tags`
        // mode commits the typed token itself. Open, cmdk's own Enter handling
        // reaches `onSelect` on the active item.
        if (!open && mode === 'tags' && searchValue) {
          event.preventDefault()
          if (!selectedValues.has(searchValue)) {
            if (maxCount == null || displayValues.length < maxCount) {
              emitChange([...displayValues, toLabeled(searchValue)])
            }
          }
          setSearchValueRaw('')
        }
        break
      case 'Backspace':
        if (multiple && !searchValue && displayValues.length) {
          const last = displayValues[displayValues.length - 1]
          if (!last.disabled) removeValue(last.value)
        }
        break
      default:
        break
    }
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const next = event.relatedTarget as Node | null
    if (
      next &&
      (rootRef.current?.contains(next) || contentRef.current?.contains(next))
    ) {
      return
    }
    setOpen(false)
    // `onBlur` BEFORE clearing the search: `TagInput` commits its pending token
    // from `onBlur` and reads the search value it still owns.
    onBlur?.(event)
    if (searchValue) setSearchValueRaw('')
  }

  // -------------------------------------------------------------------- ref
  React.useImperativeHandle(
    ref,
    () => ({
      focus: () => inputRef.current?.focus(),
      blur: () => inputRef.current?.blur(),
      // antd's `scrollTo` drives rc-virtual-list. The cmdk list is not
      // windowed, so there is nothing to scroll TO by index; the handle stays
      // callable rather than missing.
      scrollTo: () => undefined,
    }),
    []
  )

  React.useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  // ----------------------------------------------------------------- render
  const hasValue = displayValues.length > 0
  const showClear =
    Boolean(allowClear) && !disabled && (hasValue || Boolean(searchValue))
  const mergedClearIcon = (typeof allowClear === 'object'
    ? allowClear.clearIcon
    : undefined) ??
    clearIcon ?? <CloseCircleFilled />
  const showArrowIcon = showArrow ?? true
  const mergedMatchWidth = popupMatchSelectWidth ?? dropdownMatchSelectWidth

  // `maxTagCount="responsive"`: antd measures how many tags fit on one line and
  // rolls the rest into the "+ N ..." chip. Measured here rather than
  // approximated in CSS, because the rest-chip needs the real count. Under
  // jsdom every width is 0, so the measurement is a no-op and every tag stays
  // visible — tests keep seeing the whole selection.
  const tagRowRef = React.useRef<HTMLDivElement>(null)
  const tagWidthsRef = React.useRef(new Map<string, number>())
  const [responsiveCount, setResponsiveCount] = React.useState(Infinity)
  const responsive = maxTagCount === 'responsive' && multiple

  React.useLayoutEffect(() => {
    if (!responsive) return undefined
    const row = tagRowRef.current
    if (!row) return undefined

    // Widths are CACHED per tag and only refreshed while that tag is actually
    // rendered. Measuring the live row on every pass would oscillate:
    // collapsing removes tags, the ResizeObserver fires, the shorter row
    // "fits" again, and the chip flickers forever.
    const measure = () => {
      row.querySelectorAll<HTMLElement>('[data-tag-key]').forEach((node) => {
        if (node.offsetWidth) {
          tagWidthsRef.current.set(
            node.dataset.tagKey as string,
            node.offsetWidth
          )
        }
      })

      const available = row.clientWidth
      if (!available) {
        setResponsiveCount(Infinity)
        return
      }
      const restWidth =
        row.querySelector<HTMLElement>(
          '[data-slot="select-selection-overflow-item-rest"]'
        )?.offsetWidth || 44

      let used = 0
      let fits = displayValues.length
      for (let index = 0; index < displayValues.length; index += 1) {
        const width = tagWidthsRef.current.get(
          String(displayValues[index].value)
        )
        if (width === undefined) {
          // Not measured yet — show everything so the next pass can.
          fits = displayValues.length
          break
        }
        used += width + (index ? 4 : 0)
        const budget =
          index === displayValues.length - 1
            ? available
            : available - restWidth - 4
        if (used > budget) {
          fits = index
          break
        }
        fits = index + 1
      }
      setResponsiveCount(Math.max(fits, 1))
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(row)
    return () => observer.disconnect()
  }, [displayValues, responsive])

  const { visibleValues, omittedValues } = React.useMemo(() => {
    if (!multiple)
      return {
        visibleValues: displayValues,
        omittedValues: [] as DisplayValue[],
      }
    let limit: number | undefined
    if (responsive) limit = responsiveCount
    else if (typeof maxTagCount === 'number') limit = maxTagCount
    if (limit === undefined || displayValues.length <= limit) {
      return {
        visibleValues: displayValues,
        omittedValues: [] as DisplayValue[],
      }
    }
    return {
      visibleValues: displayValues.slice(0, limit),
      omittedValues: displayValues.slice(limit),
    }
  }, [displayValues, maxTagCount, multiple, responsive, responsiveCount])

  const renderTag = (item: DisplayValue, isMaxTag = false) => {
    const closable = !disabled && !item.disabled
    const onClose = (event?: React.MouseEvent<HTMLElement>) => {
      event?.preventDefault()
      event?.stopPropagation()
      removeValue(item.value)
    }
    if (tagRender) {
      return (
        <span
          data-slot="select-selection-item"
          data-tag-key={String(item.value)}
          key={String(item.value)}
        >
          {tagRender({
            label: item.label,
            value: item.value,
            disabled: Boolean(item.disabled),
            onClose,
            closable,
            isMaxTag,
          })}
        </span>
      )
    }
    return (
      <span
        data-slot="select-selection-item"
        data-tag-key={String(item.value)}
        key={String(item.value)}
        className="bg-muted inline-flex max-w-full shrink-0 items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs leading-5"
        title={typeof item.label === 'string' ? item.label : undefined}
      >
        <span className="truncate">
          {truncate(item.label, maxTagTextLength)}
        </span>
        {closable && (
          <button
            type="button"
            data-slot="select-selection-item-remove"
            aria-label="remove"
            className="text-muted-foreground hover:text-foreground inline-flex cursor-pointer items-center"
            onMouseDown={(event) => event.preventDefault()}
            onClick={onClose}
          >
            {removeIcon ?? <CloseOutlined style={{ fontSize: 10 }} />}
          </button>
        )}
      </span>
    )
  }

  const renderOptionItem = (option: NormalizedOption, index: number) => {
    const itemKey = itemKeys.get(option) as string
    const selected = selectedValues.has(option.value)
    const content = optionRender
      ? optionRender(
          {
            key: option.key,
            data: option.data,
            label: option.label,
            value: option.value,
          },
          { index }
        )
      : option.label
    return (
      <CommandItem
        key={itemKey}
        value={itemKey}
        disabled={option.disabled}
        data-slot="select-item"
        data-selected-option={selected || undefined}
        title={
          option.title ??
          (typeof option.label === 'string' ? option.label : undefined)
        }
        className={cn(
          'cursor-pointer justify-between',
          selected && 'bg-accent/60 font-medium',
          option.data.className
        )}
        onMouseDown={(event) => {
          if (!keepsOwnFocus(event.target)) event.preventDefault()
        }}
        onSelect={() => selectValue(option.value, option.label)}
      >
        <span
          data-slot="select-item-content"
          className="min-w-0 flex-1 truncate"
        >
          {content}
        </span>
        {/* antd's default `menuItemSelectedIcon`: a trailing check on every
            selected option, multiple/tags only (`useIcons`). `aria-hidden`
            keeps it out of the accessible name — the selected state already
            travels on the item's aria/data attributes. */}
        {multiple && selected && (
          <span
            aria-hidden
            data-slot="select-item-state"
            className="text-foreground ml-2 inline-flex shrink-0 items-center"
          >
            <CheckOutlined />
          </span>
        )}
      </CommandItem>
    )
  }

  const listContent = (
    <CommandList
      data-slot="select-list"
      style={{ maxHeight: listHeight }}
      onScroll={onPopupScroll}
    >
      {flat.length === 0 ? (
        <div data-slot="select-empty" className="py-2">
          {notFoundContent === undefined ? (
            // antd's own Select empty is the SIMPLE preset, not the page-sized
            // one — `withoutWrapper` drops the seam's full-height centring box,
            // which would push a two-line dropdown to the height of its parent.
            <Empty withoutWrapper image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            notFoundContent
          )}
        </div>
      ) : (
        entries.map((entry) =>
          isGroupEntry(entry) ? (
            <CommandGroup
              key={entry.key}
              data-slot="select-item-group"
              heading={entry.label as React.ReactNode}
            >
              {entry.options.map((option) =>
                renderOptionItem(option, option.index)
              )}
            </CommandGroup>
          ) : (
            renderOptionItem(entry, entry.index)
          )
        )
      )}
    </CommandList>
  )

  const dropdownBody = dropdownRender
    ? dropdownRender(listContent)
    : listContent

  // A plain input, NOT cmdk's `Command.Input`: cmdk spreads caller props BEFORE
  // its own, so it would clobber `id` (a `Form.Item`'s `<label htmlFor>` points
  // at it), `value`/`onChange` and `aria-expanded`. cmdk's keyboard navigation
  // lives on the ROOT's keydown, which this input's events bubble to, so
  // nothing is lost.
  const searchInput = (
    <input
      ref={inputRef}
      id={inputId}
      data-slot="select-search-input"
      type="text"
      role="combobox"
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-controls={listboxId}
      aria-autocomplete="list"
      {...pickAriaProps(rest as Record<string, unknown>)}
      autoComplete="off"
      disabled={disabled}
      readOnly={!showSearch}
      value={searchValue}
      onChange={handleInputChange}
      onKeyDown={handleInputKeyDown}
      onFocus={onFocus}
      onBlur={handleBlur}
      className={cn(
        'h-full w-full min-w-0 cursor-pointer appearance-none border-0 bg-transparent p-0 text-sm caret-transparent outline-hidden disabled:cursor-not-allowed',
        showSearch && 'cursor-text caret-current'
      )}
    />
  )

  // See the container decision in the header. The DEFAULT is the seam's own
  // root, which is exactly where antd's `getPopupContainer={(trigger) =>
  // trigger.parentElement}` puts it — verified against the rendered DOM, where
  // `.ant-select-dropdown` is a direct child of `.ant-select`. It matters:
  // several stylesheets reach the popup by descending from a `styled(Select)`
  // class, and that class is on the root.
  const portalContainer =
    typeof getPopupContainer === 'function'
      ? (selectorRef.current && getPopupContainer(selectorRef.current)) ??
        undefined
      : rootNode ?? undefined

  let arrowContent: React.ReactNode = null
  if (loading) arrowContent = <LoadingOutlined data-slot="select-loading" />
  else if (suffixIcon !== undefined) arrowContent = suffixIcon
  else if (showArrowIcon) arrowContent = <DownOutlined />

  const side = placement?.startsWith('top') ? 'top' : 'bottom'
  const align = placement?.endsWith('Right') ? 'end' : 'start'

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Command
        ref={setRoot}
        data-slot="select"
        data-mode={mode}
        data-disabled={disabled || undefined}
        data-open={open || undefined}
        data-status={status || undefined}
        shouldFilter={false}
        loop
        value={activeKey}
        onValueChange={setActiveKey}
        className={cn(
          'group/select relative isolate',
          'h-auto rounded-none bg-transparent text-inherit',
          inFormItem ? 'w-full' : 'inline-block',
          rootClassName,
          className
        )}
        style={style}
        {...toDomProps(rest as Record<string, unknown>)}
      >
        <PopoverAnchor asChild>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            ref={selectorRef}
            data-slot="select-selector"
            className={cn(
              'border-input dark:bg-input/30 relative flex w-full cursor-pointer flex-wrap items-center gap-1 rounded-md border bg-transparent px-3 text-sm shadow-xs transition-[color,box-shadow] outline-none',
              sizeClassName(size, multiple),
              variantClassName(
                variant ?? (bordered === false ? 'borderless' : undefined)
              ),
              disabled && 'bg-muted cursor-not-allowed opacity-60',
              status === 'error' && 'border-destructive',
              status === 'warning' && 'border-wh-gold-500',
              open && 'border-ring ring-ring/50 ring-[3px]'
            )}
            onMouseDown={(event) => {
              if (disabled) {
                event.preventDefault()
                return
              }
              if (!keepsOwnFocus(event.target)) {
                event.preventDefault()
                inputRef.current?.focus()
              }
              if ((event.target as HTMLElement).closest('button')) return
              setOpen(!open)
            }}
          >
            {!hasValue && !searchValue && (
              <span
                data-slot="select-selection-placeholder"
                className="text-muted-foreground pointer-events-none absolute inset-y-0 right-8 left-3 flex items-center truncate"
              >
                {placeholder}
              </span>
            )}

            {!multiple && hasValue && (
              <span
                data-slot="select-selection-item"
                className={cn(
                  'pointer-events-none absolute inset-y-0 right-8 left-3 flex items-center truncate',
                  searchValue && 'invisible'
                )}
                title={
                  typeof displayValues[0].label === 'string'
                    ? (displayValues[0].label as string)
                    : undefined
                }
              >
                {displayValues[0].label}
              </span>
            )}

            {multiple && (
              <div
                ref={tagRowRef}
                data-slot="select-selection-overflow"
                className={cn(
                  'flex min-w-0 flex-1 items-center gap-1',
                  responsive ? 'flex-nowrap overflow-hidden' : 'flex-wrap'
                )}
              >
                {visibleValues.map((item) => renderTag(item))}
                {omittedValues.length > 0 && (
                  <span
                    data-slot="select-selection-overflow-item-rest"
                    className="bg-muted inline-flex shrink-0 items-center rounded-sm px-1.5 py-0.5 text-xs leading-5"
                  >
                    {typeof maxTagPlaceholder === 'function'
                      ? maxTagPlaceholder(omittedValues)
                      : maxTagPlaceholder ??
                        defaultMaxTagPlaceholder(omittedValues)}
                  </span>
                )}
                <span
                  data-slot="select-selection-search"
                  className="flex min-w-[4px] flex-1 items-center"
                >
                  {searchInput}
                </span>
              </div>
            )}

            {!multiple && (
              <span
                data-slot="select-selection-search"
                className="absolute inset-y-0 right-8 left-3 flex items-center"
              >
                {searchInput}
              </span>
            )}

            <span
              data-slot="select-arrow"
              aria-hidden
              className={cn(
                'text-muted-foreground pointer-events-none absolute right-3 flex items-center text-xs',
                showClear && 'group-hover/select:invisible'
              )}
            >
              {arrowContent}
            </span>

            {showClear && (
              <button
                type="button"
                data-slot="select-clear"
                aria-label="clear"
                className="text-muted-foreground group-hover/select:flex hover:text-foreground absolute right-3 hidden cursor-pointer items-center text-xs"
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.stopPropagation()
                  clearAll()
                }}
              >
                {mergedClearIcon}
              </button>
            )}
          </div>
        </PopoverAnchor>

        <PopoverContent
          data-slot="select-dropdown"
          side={side}
          align={align}
          sideOffset={4}
          className={cn(
            'w-(--radix-popover-trigger-width) overflow-hidden p-0',
            mergedMatchWidth === false &&
              'w-auto min-w-(--radix-popover-trigger-width)',
            popupClassName,
            dropdownClassName
          )}
          style={{
            ...(typeof mergedMatchWidth === 'number'
              ? { width: mergedMatchWidth }
              : {}),
            ...dropdownStyle,
          }}
          container={portalContainer}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => {
            // The selector is "outside" the portalled content; let this
            // component's own mousedown handler own the toggle rather than
            // double-firing.
            if (rootRef.current?.contains(event.target as Node)) {
              event.preventDefault()
            }
          }}
          onFocusOutside={(event) => {
            // Focus legitimately lives OUTSIDE the content for this pattern:
            // the search input sits in the selector. Without this guard the
            // browser's post-mousedown focus lands on the input, Radix reads it
            // as a focus-outside and dismisses the popup it just opened.
            if (rootRef.current?.contains(event.target as Node)) {
              event.preventDefault()
              return
            }
            // ...and a popup opened WITHOUT focus — `defaultOpen`, or an
            // `open` a parent drives itself — has focus on `<body>`, which
            // Radix also reports as focus-outside the moment the layer mounts.
            // antd keeps such a popup open (it warns about exactly this case),
            // so nothing-is-focused is not a dismissal here either. A focus
            // that lands on a real control elsewhere still closes it.
            const target = event.target as Node | null
            if (!target || target === document.body || target === document) {
              event.preventDefault()
            }
          }}
        >
          {/* Holds the ref the blur guard checks: `PopoverContent` is a plain
              function component (no forwardRef) in `src/components/ui`. */}
          <div ref={contentRef} id={listboxId}>
            {dropdownBody}
          </div>
        </PopoverContent>
      </Command>
    </Popover>
  )
})

SelectShadcn.displayName = 'Select'

export { SelectShadcn }
